"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerProbeReconnectResolution = registerProbeReconnectResolution;
const cps_consensus_1 = require("@branch-ts/cps-consensus");
const llm_consensus_1 = require("@branch-ts/llm-consensus");
const probe_consensus_1 = require("@branch-ts/probe-consensus");
const puri_1 = require("@branch-ts/puri");
const term_consensus_1 = require("@branch-ts/term-consensus");
const web_consensus_1 = require("@branch-ts/web-consensus");
require("@branch-ts/biome-ai/src/interlock.ProbeNotReachable");
/** Reconnect only the original operation's device. Never emit retry feedback on device arrival. */
function registerProbeReconnectResolution(manager, requestProbe) {
    const sanityCheck = async ({ interlock }) => {
        const target = Util.target(interlock.detail);
        if (!target)
            return { pass: false, hint: "The original device or execution cannot be identified safely. Check the chat before starting a new operation." };
        if (target.operation === "probe") {
            if (!requestProbe)
                return { pass: false, hint: "Device request recovery is not available here." };
            try {
                const [, reason] = await requestProbe({ uri: target.probeURI });
                // Retry must also settle a deleted target with its normal NotFound
                // result, instead of trapping the conversation in reconnect forever.
                if (!reason || reason === "NotFound")
                    return { pass: true };
                if (reason === "ProbeNotReachable") {
                    return { pass: false, hint: "Reconnect the selected device in Devices, keep Probe running, then try again." };
                }
                return { pass: false, hint: reason === "ProbeNotReady"
                        ? "The selected device is still connecting or its access discovery is not ready. Try again shortly."
                        : "We could not refresh the selected device request. Check the chat and try again." };
            }
            catch {
                return { pass: false, hint: "We could not refresh the selected device. Try again shortly." };
            }
        }
        try {
            const [, reason] = await manager.sync();
            if (reason || manager.situations.get("initialization") !== "ready") {
                return { pass: false, hint: "Device connections are still loading or unavailable. Try again shortly." };
            }
        }
        catch {
            return { pass: false, hint: "We could not refresh device connections. Try again shortly." };
        }
        const entry = manager.probeEntryRegistry.get(target.probeURI);
        if (!entry?.probe?.connected || !entry.accessDiscoveryReady) {
            return { pass: false, hint: "Reconnect the original device in Devices, keep Probe running on that computer, then try again." };
        }
        const points = entry.accessPointEntries;
        const access = points.filter(point => {
            if (!point.situations.get("online"))
                return false;
            if (target.accessPointURI && point.uri !== target.accessPointURI)
                return false;
            const info = target.operation === "exec" || target.operation === "hostRead"
                ? point.info.type === "hostExec" && term_consensus_1.TermProtocol.HostURI.parse(point.uri)
                : point.info.type === "term" && term_consensus_1.TermProtocol.TerminalDomainURI.parse(point.uri);
            return info && info.vendor === target.vendor;
        });
        const filesystemReady = points.some(point => {
            if (point.info.type !== "fs" || !point.situations.get("online"))
                return false;
            const info = cps_consensus_1.CPSProtocol.FileSystemURI.parse(point.uri);
            return info?.vendor === target.vendor && (!target.fsId || info.fsId === target.fsId);
        });
        return access.length === 1 && (target.operation !== "exec" || filesystemReady)
            ? { pass: true }
            : { pass: false, hint: "The original device is connected, but its required execution/terminal or filesystem access is not ready. Check its access settings and try again." };
    };
    llm_consensus_1.InterlockRegistryMechanism.registerResolution({
        cause: "ProbeNotReachable",
        sanityCheck,
        handling: async (interlock) => {
            const result = await sanityCheck({ interlock });
            if (!result.pass)
                await web_consensus_1.CommonUI.show({ text: result.hint });
        },
    });
}
var Util;
(function (Util) {
    function target(value) {
        if (!value || typeof value !== "object")
            return null;
        const operation = "operation" in value ? value.operation : undefined;
        if (operation === "probe") {
            const probeURI = "probeURI" in value && typeof value.probeURI === "string" ? value.probeURI : undefined;
            if (!probeURI || !probe_consensus_1.ProbeProtocol.ProbeURI.parse(probeURI)?.probeId)
                return null;
            return { operation: "probe", probeURI };
        }
        const cwd = "cwd" in value && typeof value.cwd === "string" ? cps_consensus_1.CPSProtocol.FilePathFamilyURI.parse(value.cwd) : null;
        const uri = "uri" in value && typeof value.uri === "string" ? value.uri : undefined;
        const terminal = uri && (term_consensus_1.TermProtocol.TerminalURI.parse(uri) || term_consensus_1.TermProtocol.JobSliceURI.parse(uri) || term_consensus_1.TermProtocol.TerminalSliceURI.parse(uri));
        const execution = uri && term_consensus_1.TermProtocol.HostExecURI.parse(uri);
        const accessPointURI = "hostExecAccessPointURI" in value && typeof value.hostExecAccessPointURI === "string" ? value.hostExecAccessPointURI
            : "terminalAccessPointURI" in value && typeof value.terminalAccessPointURI === "string" ? value.terminalAccessPointURI : undefined;
        const host = accessPointURI && term_consensus_1.TermProtocol.HostURI.parse(accessPointURI);
        const domain = accessPointURI && term_consensus_1.TermProtocol.TerminalDomainURI.parse(accessPointURI);
        const vendor = operation === "exec" ? cwd?.vendor : operation === "read" ? terminal?.vendor || execution?.vendor : domain?.vendor;
        if (!vendor || operation === "exec" && accessPointURI && (!host || host.vendor !== vendor)
            || operation === "read" && execution && accessPointURI && (!host || host.vendor !== vendor || host.id !== execution.hostId)
            || operation !== "exec" && !execution && accessPointURI && (!domain || domain.vendor !== vendor))
            return null;
        if (operation === "exec") {
            const phase = "phase" in value ? value.phase : undefined;
            const hasExecution = "executionId" in value && typeof value.executionId === "string" && !!value.executionId;
            const hasCall = "cid" in value && typeof value.cid === "string" && !!value.cid;
            // Old post-dispatch evidence cannot prove whether replay would duplicate a command.
            if (phase !== "before-dispatch" && (!hasExecution || !hasCall))
                return null;
        }
        else if (operation !== "read" && !domain)
            return null;
        try {
            const source = puri_1.PURI.decomposeVendor(vendor);
            if (source?.protocol !== "probe" || source.kind !== "probe")
                return null;
            const probeURI = probe_consensus_1.ProbeProtocol.ProbeURI.build({ vendor: source.vendor, probeId: source.id });
            if ("probeURI" in value && value.probeURI !== undefined && value.probeURI !== probeURI)
                return null;
            const resolvedAccessPointURI = accessPointURI || (execution ? term_consensus_1.TermProtocol.HostURI.build({ vendor, id: execution.hostId }) : undefined);
            return { operation: operation === "exec" ? "exec" : execution ? "hostRead" : "read", probeURI, vendor, fsId: cwd?.fsId, accessPointURI: resolvedAccessPointURI };
        }
        catch {
            return null;
        }
    }
    Util.target = target;
})(Util || (Util = {}));
